import { DataSource } from '@angular/cdk/table';
import { BehaviorSubject, combineLatest, merge, Observable, of as observableOf, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApPaginatorComponent, PageEvent } from '@ap/paginator';

/**
 * Data source that accepts a client-side data array and includes native support of filtering,
 * sorting (using ApSort), and pagination (using ApPaginator).
 *
 * Allows for sort customization by overriding sortingDataAccessor, which defines how data
 * properties are accessed. Also allows for filter customization by overriding filterTermAccessor,
 * which defines how row data is converted to a string for filter matching.
 */
export class ApTableDataSource<T> extends DataSource<T> {
    /**
     * Subscription to the changes that should trigger an update to the table's rendered rows, such
     * as filtering, sorting, pagination, or base data changes.
     */
    _renderChangesSubscription = Subscription.EMPTY;
    /**
     * The filtered set of data that has been matched by the filter string, or all the data if there
     * is no filter. Useful for knowing the set of data the table represents.
     * For example, a 'selectAll()' function would likely want to select the set of filtered data
     * shown to the user rather than all the data.
     */
    filteredData: T[];
    /** Stream that emits when a new data array is set on the data source. */
    private readonly dataArr: BehaviorSubject<T[]>;
    /** Stream emitting render data to the table (depends on ordered data changes). */
    private readonly renderData = new BehaviorSubject<T[]>([]);
    /** Stream that emits when a new filter string is set on the data source. */
    private readonly dataFilter = new BehaviorSubject<string>('');

    private _paginator: ApPaginatorComponent | null;

    constructor(initialData: T[] = []) {
        super();
        this.dataArr = new BehaviorSubject<T[]>(initialData);
        this._updateChangeSubscription();
    }

    /** Array of data that should be rendered by the table, where each object represents one row. */
    get data(): T[] {
        return this.dataArr.value;
    }

    set data(data: T[]) {
        this.dataArr.next(data);
    }

    /**
     * Filter term that should be used to filter out objects from the data array. To override how
     * data objects match to this filter string, provide a custom function for filterPredicate.
     */
    get filter(): string {
        return this.dataFilter.value;
    }

    set filter(filter: string) {
        this.dataFilter.next(filter);
    }

    /**
     * Instance of the ApPaginatorComponent component used by the table to control what page of the data is
     * displayed. Page changes emitted by the ApPaginatorComponent will trigger an update to the
     * table's rendered data.
     *
     * Note that the data source uses the paginator's properties to calculate which page of data
     * should be displayed. If the paginator receives its properties as template inputs,
     * e.g. `[pageLength]=100` or `[pageIndex]=1`, then be sure that the paginator's view has been
     * initialized before assigning it to this data source.
     */
    get paginator(): ApPaginatorComponent | null {
        return this._paginator;
    }

    set paginator(paginator: ApPaginatorComponent | null) {
        this._paginator = paginator;
        this._updateChangeSubscription();
    }

    /**
     * Checks if a data object matches the data source's filter string. By default, each data object
     * is converted to a string of its properties and returns true if the filter has
     * at least one occurrence in that string. By default, the filter string has its whitespace
     * trimmed and the match is case-insensitive. May be overridden for a custom implementation of
     * filter matching.
     *
     * @param data Data object used to check against the filter.
     * @param filter Filter string that has been set on the data source.
     * @returns Whether the filter matches against the data
     */
    filterPredicate: (data: T, filter: string) => boolean = (data: T, filter: string): boolean => {
        // Transform the data into a lowercase string of all property values.
        const accumulator = (currentTerm: any, key: any) => currentTerm + (data as any)[key];
        const dataStr = Object.keys(data).reduce(accumulator, '').toLowerCase();

        // Transform the filter by converting it to lowercase and removing whitespace.
        const transformedFilter = filter.trim().toLowerCase();

        return dataStr.indexOf(transformedFilter) !== -1;
    };

    /**
     * Subscribe to changes that should trigger an update to the table's rendered rows. When the
     * changes occur, process the current state of the filter, sort, and pagination along with
     * the provided base data and send it to the table for rendering.
     */
    _updateChangeSubscription(): void {
        // Pagination should be watched if ApPaginatorComponent are provided.
        // The events should emit whenever the component emits a change or initializes, or if no
        // component is provided, a stream with just a null event should be provided.
        // The `sortChange` and `pageChange` acts as a signal to the combineLatests below so that the
        // pipeline can progress to the next step. Note that the value from these streams are not used,
        // they purely act as a signal to progress in the pipeline.
        const pageChange: Observable<PageEvent | null | void> = this._paginator
            ? merge(observableOf(null), this._paginator.page) // Add observableOf(null) to emit initial value
            : observableOf(null);

        const dataStream = this.dataArr;
        // Watch for base data or filter changes to provide a filtered set of data.
        const filteredData = combineLatest([dataStream, this.dataFilter]).pipe(map(([data]) => this._filterData(data)));
        // Watch for ordered data or page changes to provide a paged set of data.
        const paginatedData = combineLatest([filteredData, pageChange]).pipe(map(([data]) => this._pageData(data)));
        // Watched for paged data changes and send the result to the table to render.
        this._renderChangesSubscription.unsubscribe();
        this._renderChangesSubscription = paginatedData.subscribe((data) => this.renderData.next(data));
    }

    /**
     * Returns a filtered data array where each filter object contains the filter string within
     * the result of the filterTermAccessor function. If no filter is set, returns the data array
     * as provided.
     */
    _filterData(data: T[]): T[] {
        // If there is a filter string, filter out data that does not contain it.
        // Each data object is converted to a string using the function defined by filterTermAccessor.
        // May be overridden for customization.
        this.filteredData = !this.filter ? data : data.filter((obj) => this.filterPredicate(obj, this.filter));

        if (this.paginator) {
            this._updatePaginator(this.filteredData.length);
        }

        return this.filteredData;
    }

    /**
     * Returns a paged splice of the provided data array according to the provided ApPaginatorComponent's page
     * index and length. If there is no paginator provided, returns the data array as provided.
     */
    _pageData(data: T[]): T[] {
        if (!this.paginator) {
            return data;
        }

        const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
        return data.slice().splice(startIndex, this.paginator.pageSize);
    }

    /**
     * Updates the paginator to reflect the length of the filtered data, and makes sure that the page
     * index does not exceed the paginator's last page. Values are changed in a resolved promise to
     * guard against making property changes within a round of change detection.
     */
    _updatePaginator(filteredDataLength: number): void {
        Promise.resolve().then(() => {
            if (!this.paginator) {
                return;
            }

            this.paginator.length = filteredDataLength;

            // If the page index is set beyond the page, reduce it to the last page.
            if (this.paginator.pageIndex > 0) {
                const lastPageIndex = Math.ceil(this.paginator.length / this.paginator.pageSize) - 1 || 0;
                this.paginator.pageIndex = Math.min(this.paginator.pageIndex, lastPageIndex);
            }
        });
    }

    /**
     * Used by the ApTable. Called when it connects to the data source.
     *
     * @docs-private
     */
    connect(): BehaviorSubject<T[]> {
        return this.renderData;
    }

    /**
     * Used by the ApTable. Called when it is destroyed. No-op.
     *
     * @docs-private
     */
    disconnect(): void {}
}
